// creature-buttons.ts — client-side render of the creature card's icon-button rows,
// reading raw item_layer_types layers from window.FateTide.casterCache (carrier-walked
// by ftLayerEntries — these are genuine raw layers, NOT computed clusters, so
// ftLayerEntries/ftLayerValue are the correct readers here; cf.
// [[feedback_ftlayervalue_no_computed_bridge]] which only bites computed.creature.* values).
//
// Replaces two PHP includes, each rendered as its own inline island:
//   - options/tables/creature/status_effect_icon_buttons_inc.php
//       → renderStatusEffectButtonsInline: damage vulnerability/resistance/immunity,
//         status-effect immunity, permanent statuses, contesting-roll adv/dis vs
//         status/spells.
//   - options/tables/creature/skills_icon_buttons_inc.php
//       → renderSkillsButtonsInline: skill-tier rows, skill-roll adv/dis rows, and the
//         Spoken / Fluent / Cannot-Speak language rows. Mirrors the PHP highest-tier-wins
//         dedup + the defensive-skill / language exclusions.

import type { CachedCaster, ItemLikeRecord } from './types';
import { paintMarkup } from '../player-character-sheet/paint-markup';

type CreatureButtonsWindow = Window & {
    openItemInfo?: (itemId: string) => unknown;
    useAbility?: (itemId: string) => unknown;
    forceOpenSideInfoPanel?: () => unknown;
    renderDefenseTypeInfoPanel?: (casterId: string, layer: string, itemId: string) => unknown;
    renderStatusImmunityInfoPanel?: (casterId: string, itemId: string) => unknown;
    renderPermanentStatusInfoPanel?: (casterId: string, itemId: string) => unknown;
    renderDefenseCategoryInfoPanel?: (casterId: string, layer: string) => unknown;
};

function creatureButtonAttrs(action: string, attrs: Record<string, string> = {}): string {
    const pairs = [`data-ft-creature-button-action="${escapeHtml(action)}"`];
    for (const [key, value] of Object.entries(attrs)) {
        pairs.push(`data-ft-${key.replace(/[A-Z]/g, (m) => `-${m.toLowerCase()}`)}="${escapeHtml(value)}"`);
    }
    return pairs.join(' ');
}

function creatureInfoAttrs(itemId: string): string {
    return creatureButtonAttrs('open-info', { itemId });
}

document.addEventListener('click', (event) => {
    const el = event.target instanceof Element
        ? event.target.closest<HTMLElement>('[data-ft-creature-button-action]')
        : null;
    if (!el || !document.contains(el)) return;
    const action = el.dataset.ftCreatureButtonAction ?? '';
    const w = window as CreatureButtonsWindow;
    if (action === 'open-info') {
        const itemId = el.dataset.ftItemId ?? '';
        if (itemId !== '') { event.preventDefault(); w.openItemInfo?.(itemId); }
    } else if (action === 'use-ability') {
        const itemId = el.dataset.ftItemId ?? '';
        if (itemId !== '') { event.preventDefault(); w.useAbility?.(itemId); }
    } else if (action === 'defense-type-info') {
        event.preventDefault();
        w.forceOpenSideInfoPanel?.();
        w.renderDefenseTypeInfoPanel?.(el.dataset.ftCasterId ?? '', el.dataset.ftLayer ?? '', el.dataset.ftItemId ?? '');
    } else if (action === 'status-immunity-info') {
        event.preventDefault();
        w.forceOpenSideInfoPanel?.();
        w.renderStatusImmunityInfoPanel?.(el.dataset.ftCasterId ?? '', el.dataset.ftItemId ?? '');
    } else if (action === 'permanent-status-info') {
        event.preventDefault();
        w.forceOpenSideInfoPanel?.();
        w.renderPermanentStatusInfoPanel?.(el.dataset.ftCasterId ?? '', el.dataset.ftItemId ?? '');
    } else if (action === 'defense-category-info') {
        event.preventDefault();
        w.forceOpenSideInfoPanel?.();
        w.renderDefenseCategoryInfoPanel?.(el.dataset.ftCasterId ?? '', el.dataset.ftLayer ?? '');
    }
});

// ── Catalogs (additions.php). ─────────────────────────────────────────────────
const EXHAUSTION_ID = 'EB6FB558-7427-A647-8F46-D2E8C25F1F60';

// damage_type_defense_layer_keys() — layers whose entries are structured
// {damage_type: UUID, source_*} (or carrier shells), needing UUID extraction.
const DEFENSE_LAYER_KEYS = new Set([
    'damage_type_resistance', 'damage_type_immunity', 'damage_type_vulnerability',
    'object_damage_type_resistance', 'object_damage_type_immunity', 'object_damage_type_vulnerability',
]);

// status_effect_icon_buttons conditions, in render order, with their Bootstrap class, a
// per-category row GRADIENT (2026-06-10 defenses-tab round 2 — owner direction: no text
// sublabels, no border boxes; each row's subtle left-fading tint encodes the category:
// vulnerability=black/grey, resistance=red, immunity=white, status-immunity=gold to match
// its warning buttons, permanent=teal, contest adv=green / dis=dark red), and the label
// kept as the row icon's TOOLTIP so the meaning stays discoverable.
const STATUS_CONDITIONS: ReadonlyArray<{ layer: string; cls: string; label: string; grad: string }> = [
    { layer: 'damage_type_vulnerability', cls: 'danger text-dark', label: 'Damage Vulnerability', grad: 'linear-gradient(90deg, rgba(0,0,0,.85), rgba(130,130,130,.10))' },
    { layer: 'damage_type_resistance', cls: 'primary text-dark', label: 'Damage Resistance', grad: 'linear-gradient(90deg, rgba(220,53,69,.40), rgba(220,53,69,.04))' },
    { layer: 'damage_type_immunity', cls: 'secondary text-dark', label: 'Damage Immunity', grad: 'linear-gradient(90deg, rgba(255,255,255,.30), rgba(255,255,255,.03))' },
    { layer: 'immune_status_effect', cls: 'warning text-dark', label: 'Status-Effect Immunity', grad: 'linear-gradient(90deg, rgba(255,193,7,.30), rgba(255,193,7,.03))' },
    { layer: 'status_effect_permanent', cls: 'info text-dark', label: 'Permanent Effects', grad: 'linear-gradient(90deg, rgba(13,202,240,.28), rgba(13,202,240,.03))' },
    { layer: 'contesting_roll_adv_vs_status_effect', cls: 'light text-dark', label: 'Contest Advantage vs Status', grad: 'linear-gradient(90deg, rgba(25,135,84,.32), rgba(25,135,84,.03))' },
    { layer: 'contesting_roll_dis_vs_status_effect', cls: 'dark', label: 'Contest Disadvantage vs Status', grad: 'linear-gradient(90deg, rgba(132,32,41,.50), rgba(132,32,41,.05))' },
    { layer: 'contesting_roll_adv_vs_spells', cls: 'success text-dark', label: 'Contest Advantage vs Spells', grad: 'linear-gradient(90deg, rgba(25,135,84,.32), rgba(25,135,84,.03))' },
    { layer: 'contesting_roll_dis_vs_spells', cls: 'danger text-dark', label: 'Contest Disadvantage vs Spells', grad: 'linear-gradient(90deg, rgba(132,32,41,.50), rgba(132,32,41,.05))' },
];
// vs_spells contest layers are SCALAR flags ([0]==0 "no trait" → off) — skip when falsy.
// (They render a single per-entry button using the LAYER icon, not the entry id.)
const VS_SPELLS_LAYERS = new Set(['contesting_roll_adv_vs_spells', 'contesting_roll_dis_vs_spells']);
// vs_status contest layers are MULTI-VALUED lists of status UUIDs (a feat/passive granting
// ADV/DIS on specific statuses — e.g. "Demon Blooded" → ADV vs Poisoned). They must be gated on
// having a real status entry, NOT on ftLayerValue()===0: ftLayerValue returns the first list
// element (a UUID string → num()=0), which previously skipped every vs_status row for ALL sources
// (carrier- and feat-granted alike). Render per status id, dropping any stray scalar sentinel.
const VS_STATUS_LAYERS = new Set(['contesting_roll_adv_vs_status_effect', 'contesting_roll_dis_vs_status_effect']);

// SkillCheck::ALL — skill_tier_0..24 then the two roll-adv/dis layers.
const SKILL_CHECK_LAYERS: string[] = [
    ...Array.from({ length: 25 }, (_v, i) => `skill_tier_${i}`),
    'skill_roll_adv', 'skill_roll_dis',
];

// DefensiveSkill::getAll() item_ids — hidden on tier rows (shown in the essence/defense strip).
const DEFENSIVE_SKILL_IDS = new Set([
    '59C08B6D-F8D4-F9DB-81D9-0FD592094C73', // balance
    '24C07918-39D5-4317-A958-BDF68B633A21', // fortitude
    '58A62523-2588-452B-876B-61D8ACF3CCED', // psyche
    '922C5882-54DE-42D0-A5C8-8118AA1C858E', // willpower
]);

// LanguageSkill::getAll() item_ids — rendered only in the Spoken/Fluent rows, never on tier rows.
const LANGUAGE_SKILL_IDS = new Set([
    'A5B19883-CBDA-EC46-A859-96FF01651278', 'B12FE4D0-480A-239A-DAF0-704850F5162D',
    '820AB0A3-F21A-3135-DB75-89DCDF09A9D0', 'B1B5365D-2DE0-AA53-A729-AC28BF8C8D52',
    '81DADED4-7909-482A-BE32-76887642C172', '6EEF7655-4FC7-F5F4-0AB8-C31C31FEEDCD',
    '0700727F-6C9E-4631-1140-B57A8E442137', '94D1A00F-4795-B1DD-6981-6CE3364AF1B3',
    '5C346F0F-D58D-2859-92EB-A347FA56899E', '42728623-5523-5986-672E-9A9FE5DD35B5',
    '4A5172E6-6D97-3958-C65B-F0792A660B85', '3098C163-7B26-7757-24F1-129C467D5BDB',
    '6AB76ADB-22BC-904A-7D72-43FDCA112DD1', '4767BD78-9829-6FC9-BD4A-5855F322CEB7',
    '22CC3970-0458-63CA-3D69-82A746B9A5CD', '16A53E58-027B-DF11-6AC5-72CA13B6583A',
    '2CB279E7-CD79-1A21-9F32-89184886D7FC', '641E712F-1B60-C633-14BF-C4EE0DDD4AF4',
    'FBD22CAB-950F-8DAE-E052-65A88BF5205B', 'D267CD08-6952-99E9-B19E-52EC690546BB',
    'EAC11C79-B399-7259-8556-8EDA88B7FCD8', 'E76097FE-D47E-FD76-B7AF-8F0C40A3BCA1',
    'ABAE4F65-1A42-DEFC-21C8-11EBF883A484',
]);

// ── Shared readers. ───────────────────────────────────────────────────────────
function num(v: unknown): number {
    const n = Number(Array.isArray(v) ? v[0] : v);
    return Number.isFinite(n) ? n : 0;
}

/** UUID-shaped string guard — used to drop scalar sentinels (legacy [0] "no trait") from the
 *  vs_status contest rows so they never render a phantom button. */
const UUID_RE = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
function isUuid(v: string): boolean {
    return UUID_RE.test(v);
}

/** item_arr_scalar_layer_slot / layer_list_entry_uuid twin: a layer entry → its UUID. */
function entryUuid(entry: unknown): string {
    if (entry == null) return '';
    if (typeof entry === 'string') return entry.trim();
    if (typeof entry === 'number') return String(entry);
    if (Array.isArray(entry)) return entryUuid(entry[0]);
    if (typeof entry === 'object') {
        const o = entry as ItemLikeRecord;
        // item_layer_effect carries the UUID on SOURCED status/contesting defense structs
        // ({item_layer_effect, source_*}) added 2026-06-21 — mirror PHP layer_list_entry_uuid.
        const raw = o.items?.item_id ?? o.item_id ?? o.item_layer_effect ?? o.damage_type ?? '';
        return String(Array.isArray(raw) ? raw[0] : (raw ?? '')).trim();
    }
    return '';
}

/** get_damage_type_defense_ids twin: structured defense entries → unique damage_type UUIDs. */
function damageTypeDefenseIds(caster: CachedCaster, layer: string): string[] {
    const out: string[] = [];
    for (const entry of ftLayerEntries(caster, layer)) {
        if (entry && typeof entry === 'object' && !Array.isArray(entry)) {
            const dt = (entry as ItemLikeRecord).damage_type;
            if (typeof dt === 'string' && dt !== '') { out.push(dt); continue; }
            const uid = entryUuid(entry);
            if (uid) out.push(uid);
        } else if (typeof entry === 'string' && entry !== '') {
            out.push(entry);
        }
    }
    return [...new Set(out)];
}

/** UUIDs of a flat (non-defense) layer's entries (scalars or shells), deduped. Dedup matters now
 *  that defense layers append (computed store ∪ carrier walk): a status immune via BOTH a permanent
 *  status and a creature_type carrier would otherwise render a duplicate icon. */
function layerUuids(caster: CachedCaster, layer: string): string[] {
    const out: string[] = [];
    for (const entry of ftLayerEntries(caster, layer)) {
        const uid = entryUuid(entry);
        if (uid !== '') out.push(uid);
    }
    return [...new Set(out)];
}

// ── STATUS-EFFECT ICON BUTTONS ────────────────────────────────────────────────
function renderStatusEffectButtons(caster: CachedCaster, casterId: string): string {
    // casterId is threaded into the defense onclicks so the source-breakdown panels
    // (defenses-info.ts) can read the right blob from casterCache. Non-defense rows keep
    // openItemInfo() unchanged.
    const cid = escapeHtml(casterId);
    let rows = '';
    for (const { layer, cls, label, grad } of STATUS_CONDITIONS) {
        const entries = ftLayerEntries(caster, layer);
        if (entries.length === 0) continue;
        // Scalar vs_spells flags: off when [0]==0. (vs_status lists are gated on real entries below.)
        if (VS_SPELLS_LAYERS.has(layer) && num(ftLayerValue(caster, layer)) === 0) continue;

        const isDefenseRow = DEFENSE_LAYER_KEYS.has(layer);
        const isStatusImmunityRow = layer === 'immune_status_effect';
        const isPermanentRow = layer === 'status_effect_permanent';
        // contesting_roll_*_vs_status_effect carry SOURCED entries (2026-06-21) — give them the same
        // per-status source trace + category breakdown as the other defense rows.
        const isContestStatusRow = VS_STATUS_LAYERS.has(layer);
        // Every "where does this come from" row whose subheader opens a category breakdown.
        const hasBreakdown = isDefenseRow || isStatusImmunityRow || isPermanentRow || isContestStatusRow;
        let ids = isDefenseRow
            ? damageTypeDefenseIds(caster, layer)
            : layerUuids(caster, layer);
        // vs_status rows render one button per status UUID — drop any scalar sentinel so a legacy
        // [0] "no trait" value never produces a phantom icon button.
        if (VS_STATUS_LAYERS.has(layer)) ids = ids.filter(isUuid);
        if (ids.length === 0) continue;

        let buttons = '';
        for (const id of ids) {
            if (VS_SPELLS_LAYERS.has(layer)) {
                buttons += `<button type="button" class="btn btn-${cls} btn-sm m-1">${iconMap(layer)}</button>`;
            } else if (isDefenseRow) {
                // Damage resist/immunity/vulnerability — per-type source panel. No button tooltip:
                // iconMap() already renders the damage type's own name tooltip (a 2nd would double-popup).
                buttons += `<button type="button" class="btn btn-${cls} btn-sm m-1" ${creatureButtonAttrs('defense-type-info', { casterId, layer, itemId: id })}>${iconMap(id)}</button>`;
            } else if (isStatusImmunityRow) {
                // Status-effect immunity — panel: the status + which source grants the immunity.
                buttons += `<button type="button" class="btn btn-${cls} btn-sm m-1" ${creatureButtonAttrs('status-immunity-info', { casterId, itemId: id })}>${iconMap(id)}</button>`;
            } else if (isPermanentRow) {
                // Permanent (always-on) status — panel: what it grants (damage + status immunities).
                buttons += `<button type="button" class="btn btn-${cls} btn-sm m-1" ${creatureButtonAttrs('permanent-status-info', { casterId, itemId: id })}>${iconMap(id)}</button>`;
            } else if (isContestStatusRow) {
                // Contest adv/dis vs a status — per-status source trace (reuses the generic defense
                // trace panel with this contesting layer; e.g. "Passive Ability › Stillblood › Bleeding").
                buttons += `<button type="button" class="btn btn-${cls} btn-sm m-1" ${creatureButtonAttrs('defense-type-info', { casterId, layer, itemId: id })}>${iconMap(id)}</button>`;
            } else if (id === EXHAUSTION_ID) {
                buttons += `<button type="button" class="btn btn-${cls} btn-sm m-1" ${creatureInfoAttrs(id)}>${iconMap(id)}</button>`;
            } else {
                buttons += `<button type="button" class="btn btn-${cls} btn-sm m-1" ${creatureInfoAttrs(id)}>${iconMap(id)}</button>`;
            }
        }

        // Borderless gradient band per category; the icon's tooltip carries the label. For rows
        // with a breakdown the category icon is a button → full source breakdown for the category.
        const headerIcon = hasBreakdown
            ? `<div class="flex-shrink-0 me-2" role="button" ${creatureButtonAttrs('defense-category-info', { casterId, layer })}>${iconMap(layer, 24, 24, `${label} — show all`)}</div>`
            : `<div class="flex-shrink-0 me-2">${iconMap(layer, 24, 24, label)}</div>`;

        rows += `<div class="d-flex align-items-center py-1 px-2 rounded mb-1" style="background:${grad};">
            ${headerIcon}
            <div class="d-flex flex-wrap align-items-center min-w-0">${buttons}</div>
        </div>`;
    }
    return rows;
}

// ── SKILLS ICON BUTTONS ───────────────────────────────────────────────────────
/** skill_tier_N → N (skill_tier_level); skill_roll_adv/dis (or anything else) → null. */
function tierLevelOfLayer(layer: string): number | null {
    const m = /^skill_tier_(\d+)$/.exec(layer);
    return m ? Number(m[1]) : null;
}

function skillsButtonsClass(layer: string): string {
    switch (layer) {
        case 'skill_tier_1': case 'skill_tier_2': return 'light text-dark';
        case 'skill_tier_3': case 'skill_tier_4': return 'primary text-dark';
        case 'skill_tier_5': case 'skill_tier_6': return 'secondary text-dark';
        case 'skill_tier_7': case 'skill_tier_8': return 'warning text-dark';
        case 'skill_tier_9': case 'skill_tier_10': return 'info text-dark';
        case 'skill_roll_adv': return 'success';
        case 'skill_roll_dis': return 'danger';
        default: return 'info text-dark';
    }
}

function renderSkillsButtons(caster: CachedCaster): string {
    // Highest-tier-wins map: skill uuid → highest skill_tier level it appears in.
    const highestTier: Record<string, number> = {};
    for (const layer of SKILL_CHECK_LAYERS) {
        const lvl = tierLevelOfLayer(layer);
        if (lvl === null) continue;
        for (const uuid of layerUuids(caster, layer)) {
            if (highestTier[uuid] === undefined || lvl > highestTier[uuid]) highestTier[uuid] = lvl;
        }
    }

    // First rendered skill/language row gets a top border (divider from the status section).
    let firstRow = true;
    const rowBorder = (): string => {
        if (firstRow) { firstRow = false; return ' border-top'; }
        return '';
    };

    // A layer renders only if at least one of its skills survives the three skip rules.
    const renderable = (layer: string, skills: string[]): string[] => {
        const thisLevel = tierLevelOfLayer(layer);
        const isAdvDis = layer === 'skill_roll_adv' || layer === 'skill_roll_dis';
        return skills.filter((s) => {
            if (thisLevel !== null && highestTier[s] !== undefined && highestTier[s] > thisLevel) return false;
            if (DEFENSIVE_SKILL_IDS.has(s) && !isAdvDis) return false;
            if (LANGUAGE_SKILL_IDS.has(s)) return false;
            return true;
        });
    };

    let html = '';

    // Tier + adv/dis rows.
    for (const layer of SKILL_CHECK_LAYERS) {
        const skills = layerUuids(caster, layer);
        if (renderable(layer, skills).length === 0) continue;

        const cls = skillsButtonsClass(layer);
        const thisLevel = tierLevelOfLayer(layer);
        const isAdvDis = layer === 'skill_roll_adv' || layer === 'skill_roll_dis';

        let buttons = '';
        for (const s of skills) {
            if (thisLevel !== null && highestTier[s] !== undefined && highestTier[s] > thisLevel) continue;
            if (DEFENSIVE_SKILL_IDS.has(s) && !isAdvDis) continue;
            if (LANGUAGE_SKILL_IDS.has(s)) continue;
            buttons += `<button type="button" class="btn btn-${cls} btn-sm m-1" ${creatureButtonAttrs('use-ability', { itemId: s })}>${iconMap(s)}</button>`;
        }

        html += `<div class="d-flex text-center align-items-center${rowBorder()}" style="min-height:50px;">
            <div class="col-2 border-end" style="min-width:50px;">${iconMap(layer)}</div>
            <div class="col text-left pl-1">${buttons}</div>
        </div>`;
    }

    // Spoken languages (skill_tier_1, not overridden by a higher tier).
    const tier1 = layerUuids(caster, 'skill_tier_1');
    const spoken = tier1.filter((u) => (highestTier[u] ?? 1) <= 1 && LANGUAGE_SKILL_IDS.has(u));
    if (spoken.length > 0) {
        let buttons = '';
        for (const s of spoken) {
            buttons += `<button type="button" class="btn btn-dark border btn-sm m-1" ${creatureInfoAttrs(s)}>${iconMap(s)}</button>`;
        }
        html += `<div class="d-flex text-center align-items-center${rowBorder()}" style="min-height:50px;">
            <div class="col-2 border-end" style="min-width:50px;">${iconMap('language_spoken')}</div>
            <div class="col text-left pl-1">${buttons}</div>
        </div>`;
    }

    // Fluent languages (skill_tier_2).
    const tier2 = layerUuids(caster, 'skill_tier_2');
    const fluent = tier2.filter((u) => LANGUAGE_SKILL_IDS.has(u));
    if (fluent.length > 0) {
        let buttons = '';
        for (const s of fluent) {
            buttons += `<button type="button" class="btn btn-light btn-sm m-1" ${creatureInfoAttrs(s)}>${iconMap(s)}</button>`;
        }
        html += `<div class="d-flex text-center align-items-center${rowBorder()}" style="min-height:50px;">
            <div class="col-2 border-end" style="min-width:50px;">${iconMap('language_fluent')}</div>
            <div class="col text-left pl-1">${buttons}</div>
        </div>`;
    }

    // Blanket language knowledge (2026-07-15). These are the flag alternative to listing a
    // Spoken/Fluent button per language, so they render as their own rows rather than as buttons —
    // there is no per-language item to open. reads_all_languages is the mechanical one (it
    // short-circuits caster_can_read_written_language); speaks_all_languages is descriptive.
    // Scalar flags → ftLayerValue + num, not ftLayerEntries (cf. the descalar note at the top).
    if (num(ftLayerValue(caster, 'speaks_all_languages')) !== 0) {
        html += `<div class="d-flex text-center align-items-center${rowBorder()}" style="min-height:50px;">
            <div class="col-2 border-end" style="min-width:50px;">${iconMap('speaks_all_languages')}</div>
            <div class="col text-left pl-1">Speaks and understands every language.</div>
        </div>`;
    }
    if (num(ftLayerValue(caster, 'reads_all_languages')) !== 0) {
        html += `<div class="d-flex text-center align-items-center${rowBorder()}" style="min-height:50px;">
            <div class="col-2 border-end" style="min-width:50px;">${iconMap('reads_all_languages')}</div>
            <div class="col text-left pl-1">Reads every written language.</div>
        </div>`;
    }

    // Cannot-speak languages (icon-only row).
    if (ftLayerEntries(caster, 'language_cannot_speak').length > 0) {
        html += `<div class="d-flex text-center align-items-center${rowBorder()}" style="min-height:50px;">
            <div class="col-2" style="min-width:50px;">${iconMap('language_cannot_speak')}</div>
        </div>`;
    }

    return html;
}

// ── Inline entry points (mirror renderCreatureSummaryInline). ─────────────────
async function renderButtonsInline(
    casterId: string,
    targetId: string,
    build: (caster: CachedCaster) => string,
): Promise<void> {
    const target = document.getElementById(targetId);
    if (!target) return;
    let caster = window.FateTide?.casterCache?.get(casterId) as CachedCaster | undefined;
    if (!caster) {
        try {
            await fetchAndCacheCaster(casterId);
        } catch {
            // fetchAndCacheCaster swallows its own errors; fall through to the miss guard.
        }
        caster = window.FateTide?.casterCache?.get(casterId) as CachedCaster | undefined;
    }
    paintMarkup(target, caster ? build(caster) : '');
}

export function renderStatusEffectButtonsInline(casterId: string, targetId: string): Promise<void> {
    return renderButtonsInline(casterId, targetId, (caster) => renderStatusEffectButtons(caster, casterId));
}
// RETIRED 2026-06-09: renderSkillsButtonsInline / renderSkillsButtons — the creature card's skills
// button strip moved to the console Skills tab (PC-sheet skills pane); no caller remains. The builder
// below is now unreferenced (tree-shaken from the bundle). [[project_creature_renderer_unification]]
